import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule, MatSelectionListChange } from "@angular/material/list";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { map } from "rxjs";

import { SearchBoxComponent } from "components/inputs/search-box/search-box.component";
import { provideMatFormFieldConfig } from "features/score-types/shared/providers/mat-form-field-config.provider";
import { OrganizationStats } from "models/Organization";
import { OrgListFacade } from "state/org-list.facade";
import { OrgFacade } from "state/org.facade";
import { UserFacade } from "state/user.facade";

export type OrgEntry = {
  _id: string;
  name: string;
};

@Component({
  selector: "dev-org-selector",
  templateUrl: "./dev-org-selector.component.html",
  styleUrls: ["./dev-org-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    TranslateModule,
    SearchBoxComponent,
  ],
  providers: [provideMatFormFieldConfig()],
  host: {
    class: "inseer-ds",
  },
})
export class DevOrgSelectorComponent implements AfterViewInit {
  private readonly orgListFacade = inject(OrgListFacade);
  private readonly orgFacade = inject(OrgFacade);
  private readonly userFacade = inject(UserFacade);
  private readonly translate = inject(TranslateService);

  /** Emits the picked org id so the containing menu can close itself. */
  public readonly orgSelected = output<OrgEntry>();

  private readonly orgs = toSignal<OrganizationStats[] | null | undefined>(
    this.orgListFacade.activeOrgs$
  );

  private readonly sortedOrgs = computed<OrganizationStats[]>(() => {
    return this.orgs()?.toSorted((a, b) => a.name.localeCompare(b.name)) ?? [];
  });

  protected readonly loadingOrgs = computed<boolean>(() => {
    return this.orgs() === undefined;
  });

  private readonly currentOrgId = toSignal<string | null>(this.orgFacade.orgId$, {
    initialValue: null,
  });

  private readonly userOrgId = toSignal<string | null>(
    this.userFacade.user$.pipe(map((user) => user?.organization ?? null)),
    { initialValue: null }
  );

  protected readonly searchQuery = signal<string>("");

  // Seeded from the active org; updated locally as the user picks a row.
  protected readonly selectedOrgId = linkedSignal<string | null>(() =>
    this.currentOrgId()
  );

  protected readonly filteredOrgs = computed<OrganizationStats[]>(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const orgs = this.sortedOrgs();

    if (!query) {
      return orgs;
    }

    return orgs.filter((org) => this.orgDisplayName(org).toLowerCase().includes(query));
  });

  protected readonly isNonHomeOrgSelected = computed<boolean>(() => {
    const selectedOrgId = this.selectedOrgId();
    return selectedOrgId != null && selectedOrgId !== this.userOrgId();
  });

  private readonly searchBox = viewChild(SearchBoxComponent);

  public ngAfterViewInit(): void {
    this.searchBox()?.focus();
  }

  protected orgDisplayFn(org: OrganizationStats): string {
    const orgName = this.orgDisplayName(org);

    if (org._id === this.userOrgId()) {
      const homeOrgSuffix = this.translate.instant(
        "shared.navigation_bar.org_selector.home_org_suffix"
      );
      return `${orgName} (${homeOrgSuffix})`;
    }

    return orgName;
  }

  protected onSelectionChange(event: MatSelectionListChange): void {
    const selectedOption = event.options.find((option) => option.selected);

    if (selectedOption) {
      const orgId = selectedOption.value as string;
      this.selectedOrgId.set(orgId);
      this.notifyOrgSelected(orgId);
    }
  }

  protected switchToHomeOrg(): void {
    const homeOrgId = this.userOrgId();

    if (homeOrgId != null) {
      this.selectedOrgId.set(homeOrgId);
      this.notifyOrgSelected(homeOrgId);
    }
  }

  private notifyOrgSelected(orgId: string): void {
    const orgStats = this.orgs()?.find((org) => org._id === orgId);
    if (orgStats) {
      const org: OrgEntry = { _id: orgStats._id, name: orgStats.name };
      this.orgSelected.emit(org);
    }
    this.clearSearchQuery();
  }

  private orgDisplayName(org: OrganizationStats): string {
    return org.translatedName ?? org.name ?? `Org ${org._id}`;
  }

  private clearSearchQuery(): void {
    this.searchQuery.set("");
  }
}
